import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import ConfigDB from "../../config";
import Utils from "../../utils";
import {
  getLotsList,
  createLot,
  updateLot,
  deleteLot,
} from "../../services/LotsServices";

// ============================================================
// Lots — listagem em tabela full-width (mesmo layout das peças/
// viaturas) com filtros no topo. Cada linha: imagem, ID TP, nome,
// estado armazenamento, contadores, status e acções.
// ============================================================

const LOT_STATUS = [
  { id: 1, key: "active" },
  { id: 2, key: "deleted" },
];
const STORAGE_STATUS = [
  { id: 1, key: "reserved_at_supplier" },
  { id: 2, key: "in_transport" },
  { id: 3, key: "stored_at_facilities" },
];
const PARTS_MGMT = [
  { id: 1, key: "with_parts" },
  { id: 2, key: "without_parts" },
];

const Lots = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const RolePermission = Utils.getRecordForCurrentPath(location);
  const tableLengthList = ConfigDB.data.dataTableLength;
  const data = localStorage.getItem(ConfigDB.data.login_data);
  const userData = JSON.parse(data) || {};

  const [params, setParams] = useState({
    limit: tableLengthList[0],
    page: 1,
    search: "",
    status: 0,
    storage_status: 0,
    parts_management: 0,
    company_id: userData.company_id || 0,
  });
  const [lotList, setLotList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [modifyModal, setModifyModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lotForm, setLotForm] = useState({
    id: "",
    name: "",
    lot_internal_id: "",
    status: 1,
    storage_status: 1,
    purchase_date: "",
    max_sale_date: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  const [delModal, setDelModal] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [isDelLoad, setIsDelLoad] = useState(false);

  // ── Fetch ──
  const fetchLots = () => {
    const payload = { ...params };
    if (!payload.status) delete payload.status;
    if (!payload.storage_status) delete payload.storage_status;
    if (!payload.parts_management) delete payload.parts_management;
    getLotsList(payload)
      .then((res) => {
        setLotList(res.data.data || []);
        setTotalRecords(res.data?.pagination?.total || 0);
      })
      .catch((err) => {
        toast.dismiss();
        toast.error(Utils.getErrorMessage(err));
      });
  };

  useEffect(() => {
    fetchLots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.page,
    params.limit,
    params.search,
    params.status,
    params.storage_status,
    params.parts_management,
  ]);

  const handleParam = (e, type) => {
    const next = { ...params };
    if (type === "length") next.limit = parseInt(e.target.value);
    else if (type === "search") {
      next.page = 1;
      next.search = e.target.value;
    } else if (type === "pagination") {
      next.page = e.selected + 1;
    } else if (
      type === "status" ||
      type === "storage_status" ||
      type === "parts_management"
    ) {
      next.page = 1;
      next[type] = parseInt(e.target.value);
    }
    setParams(next);
  };

  const openAddModal = () => {
    setLotForm({
      id: "",
      name: "",
      lot_internal_id: "",
      status: 1,
      storage_status: 1,
      purchase_date: "",
      max_sale_date: "",
      notes: "",
    });
    setErrors({});
    setModifyModal(true);
  };

  const openEditModal = (lot) => {
    setLotForm({
      id: lot.id,
      name: lot.name || "",
      lot_internal_id: lot.lot_internal_id || "",
      status: lot.status || 1,
      storage_status: lot.storage_status || 1,
      purchase_date: (lot.purchase_date || "").substring(0, 10),
      max_sale_date: (lot.max_sale_date || "").substring(0, 10),
      notes: lot.notes || "",
    });
    setErrors({});
    setModifyModal(true);
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!lotForm.name?.trim()) errs.name = "required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setIsSaving(true);
    const payload = {
      name: lotForm.name.trim(),
      status: lotForm.status,
      storage_status: lotForm.storage_status,
      purchase_date: lotForm.purchase_date || null,
      max_sale_date: lotForm.max_sale_date || null,
      notes: lotForm.notes || null,
    };
    if (lotForm.id) {
      payload.id = lotForm.id;
      payload.lot_internal_id = lotForm.lot_internal_id;
    }
    const promise = lotForm.id ? updateLot(payload) : createLot(payload);
    promise
      .then((res) => {
        toast.dismiss();
        toast.success(res.data?.message || t("Saved"));
        setIsSaving(false);
        setModifyModal(false);
        fetchLots();
      })
      .catch((err) => {
        setIsSaving(false);
        toast.dismiss();
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const handleDelete = () => {
    if (!delTarget?.id) return;
    setIsDelLoad(true);
    deleteLot({ id: delTarget.id })
      .then((res) => {
        toast.dismiss();
        toast.success(res.data?.message || t("Deleted"));
        setDelModal(false);
        setIsDelLoad(false);
        fetchLots();
      })
      .catch((err) => {
        setIsDelLoad(false);
        toast.dismiss();
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const localStatus = (s) => {
    const m = LOT_STATUS.find((x) => x.id === s);
    return m ? t(m.key) : "";
  };
  const localStorage_ = (s) => {
    const m = STORAGE_STATUS.find((x) => x.id === s);
    return m ? t(m.key) : "";
  };
  const fmtDate = (s) => (s ? s.substring(0, 10) : "—");

  // ── Render ──
  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6"></div>
            <div className="col-sm-6"></div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid container-custom-height-new">
          <div className="row">
            <div className="col-md-12">
              <div className="card fuelCard">
                <div className="card-header">
                  <div className="d-flex flex-wrap align-items-center gap-2 w-100">
                    {/* Search */}
                    <div className="position-relative" style={{ flex: "1 1 240px", maxWidth: 360 }}>
                      <i
                        className="bi bi-search position-absolute"
                        style={{ top: 10, left: 12, color: "#6c757d" }}
                      ></i>
                      <input
                        type="text"
                        className="form-control"
                        style={{ paddingLeft: 36 }}
                        placeholder={t("search")}
                        value={params.search}
                        onChange={(e) => handleParam(e, "search")}
                      />
                    </div>
                    {/* Filter — Status */}
                    <select
                      className="form-select"
                      style={{ width: "auto", minWidth: 140 }}
                      value={params.status}
                      onChange={(e) => handleParam(e, "status")}
                    >
                      <option value={0}>{t("state")}: {t("all")}</option>
                      {LOT_STATUS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {t(s.key)}
                        </option>
                      ))}
                    </select>
                    {/* Filter — Storage */}
                    <select
                      className="form-select"
                      style={{ width: "auto", minWidth: 200 }}
                      value={params.storage_status}
                      onChange={(e) => handleParam(e, "storage_status")}
                    >
                      <option value={0}>{t("storage_status")}: {t("all")}</option>
                      {STORAGE_STATUS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {t(s.key)}
                        </option>
                      ))}
                    </select>
                    {/* Filter — Parts mgmt */}
                    <select
                      className="form-select"
                      style={{ width: "auto", minWidth: 170 }}
                      value={params.parts_management}
                      onChange={(e) => handleParam(e, "parts_management")}
                    >
                      <option value={0}>{t("parts_management")}: {t("all")}</option>
                      {PARTS_MGMT.map((s) => (
                        <option key={s.id} value={s.id}>
                          {t(s.key)}
                        </option>
                      ))}
                    </select>
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <select
                        className="form-select"
                        style={{ width: "auto" }}
                        value={params.limit}
                        onChange={(e) => handleParam(e, "length")}
                      >
                        {tableLengthList.map((n) => (
                          <option key={n} value={n}>
                            {n} {t("per_page") || "por página"}
                          </option>
                        ))}
                      </select>
                      <Button color="primary" onClick={openAddModal}>
                        <i className="bi bi-plus-lg me-1" /> {t("add_lot")}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 70 }}></th>
                          <th>{t("lot_internal_id")}</th>
                          <th>{t("name")}</th>
                          <th>{t("storage_status")}</th>
                          <th className="text-center">
                            <i className="bi bi-box-seam" title={t("parts")} />
                          </th>
                          <th className="text-center">
                            <i className="bi bi-truck" title={t("vehicles")} />
                          </th>
                          <th className="text-center">
                            <i className="bi bi-building" title={t("suppliers")} />
                          </th>
                          <th>{t("purchase_date")}</th>
                          <th>{t("state")}</th>
                          <th className="text-end" style={{ width: 110 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lotList.length === 0 && (
                          <tr>
                            <td
                              colSpan={10}
                              className="text-center text-muted py-5"
                            >
                              {t("no_data_found")}
                            </td>
                          </tr>
                        )}
                        {lotList.map((lot) => {
                          const isDeleted = lot.status === 2;
                          return (
                            <tr
                              key={lot.id}
                              style={{
                                cursor: "pointer",
                                opacity: isDeleted ? 0.65 : 1,
                              }}
                              onClick={() =>
                                navigate(`/lot-details/${lot.uuid || lot.id}`)
                              }
                            >
                              <td>
                                <div
                                  style={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: 8,
                                    overflow: "hidden",
                                    background: "#f0f2f5",
                                  }}
                                >
                                  {lot.image_preview ? (
                                    <img
                                      src={lot.image_preview}
                                      alt=""
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center text-muted h-100"
                                      style={{ fontSize: 20 }}
                                    >
                                      <i className="bi bi-archive" />
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                {lot.lot_internal_id ? (
                                  <span className="badge bg-primary">
                                    #{lot.lot_internal_id}
                                  </span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                <strong>{lot.name || "—"}</strong>
                              </td>
                              <td>
                                <span className="text-muted small">
                                  {localStorage_(lot.storage_status) || "—"}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {lot.parts_count || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {lot.vehicles_count || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {lot.suppliers_count || 0}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted small">
                                  {fmtDate(lot.purchase_date)}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    isDeleted ? "bg-danger" : "bg-success"
                                  }`}
                                >
                                  {localStatus(lot.status)}
                                </span>
                              </td>
                              <td
                                className="text-end"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-info p-1"
                                  title={t("view") || "Ver"}
                                  onClick={() =>
                                    navigate(
                                      `/lot-details/${lot.uuid || lot.id}`,
                                    )
                                  }
                                >
                                  <i className="bi bi-eye fs-5" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-primary p-1"
                                  title={t("edit") || "Editar"}
                                  onClick={() => openEditModal(lot)}
                                >
                                  <i className="bi bi-pencil-square fs-5" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-1"
                                  title={t("delete") || "Eliminar"}
                                  onClick={() => {
                                    setDelTarget(lot);
                                    setDelModal(true);
                                  }}
                                >
                                  <i className="bi bi-trash3 fs-5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer w/ pagination */}
                {totalRecords > params.limit && (
                  <div className="card-footer d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted small">
                      {t("showing") || "A mostrar"} {(params.page - 1) * params.limit + 1}–
                      {Math.min(params.page * params.limit, totalRecords)}{" "}
                      {t("of") || "de"} {totalRecords}
                    </div>
                    <ReactPaginate
                      pageCount={Math.ceil(totalRecords / params.limit)}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={1}
                      onPageChange={(e) => handleParam(e, "pagination")}
                      containerClassName="pagination mb-0"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      activeClassName="active"
                      forcePage={params.page - 1}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add/Edit Modal */}
      <Modal isOpen={modifyModal} toggle={() => setModifyModal(false)} centered size="md">
        <Form onSubmit={submit}>
          <ModalHeader toggle={() => setModifyModal(false)}>
            {lotForm.id ? t("edit_lot") : t("add_lot")}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("name")}</Label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                value={lotForm.name}
                onChange={(e) => setLotForm({ ...lotForm, name: e.target.value })}
              />
            </FormGroup>
            {lotForm.id && (
              <FormGroup>
                <Label>{t("lot_internal_id")}</Label>
                <input
                  type="text"
                  className="form-control"
                  value={lotForm.lot_internal_id}
                  onChange={(e) =>
                    setLotForm({ ...lotForm, lot_internal_id: e.target.value })
                  }
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label>{t("storage_status")}</Label>
              <select
                className="form-select"
                value={lotForm.storage_status}
                onChange={(e) =>
                  setLotForm({ ...lotForm, storage_status: parseInt(e.target.value) })
                }
              >
                {STORAGE_STATUS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {t(s.key)}
                  </option>
                ))}
              </select>
            </FormGroup>
            {lotForm.id && (
              <FormGroup>
                <Label>{t("state")}</Label>
                <select
                  className="form-select"
                  value={lotForm.status}
                  onChange={(e) =>
                    setLotForm({ ...lotForm, status: parseInt(e.target.value) })
                  }
                >
                  {LOT_STATUS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {t(s.key)}
                    </option>
                  ))}
                </select>
              </FormGroup>
            )}
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label>{t("purchase_date")}</Label>
                  <input
                    type="date"
                    className="form-control"
                    value={lotForm.purchase_date}
                    onChange={(e) =>
                      setLotForm({ ...lotForm, purchase_date: e.target.value })
                    }
                  />
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label>{t("max_sale_date")}</Label>
                  <input
                    type="date"
                    className="form-control"
                    value={lotForm.max_sale_date}
                    onChange={(e) =>
                      setLotForm({ ...lotForm, max_sale_date: e.target.value })
                    }
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <Label>{t("notes")}</Label>
              <textarea
                className="form-control"
                rows={3}
                value={lotForm.notes}
                onChange={(e) => setLotForm({ ...lotForm, notes: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={() => setModifyModal(false)}>
              {t("cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              {isSaving ? t("saving") : t("save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={delModal} toggle={() => setDelModal(false)} centered>
        <ModalHeader toggle={() => setDelModal(false)}>{t("delete_lot")}</ModalHeader>
        <ModalBody>{t("are_you_sure_delete_lot")}</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDelModal(false)}>
            {t("label_no")}
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={isDelLoad}>
            {isDelLoad ? "..." : t("label_yes")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Lots;
